import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Video, Mic, CheckCircle, Play } from 'lucide-react';

const steps = [
  { key: 'welcome', label: 'Welcome', icon: Play },
  { key: 'setup', label: 'Device Check', icon: Mic },
  { key: 'recording', label: 'Interview', icon: Video },
  { key: 'complete', label: 'Complete', icon: CheckCircle },
];

const languages = ['English', 'Português', 'Español', 'Français', 'Deutsch'];

export function LandingInterviewDemo() {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // Auto-advance through steps
  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 3000);

    return () => clearInterval(stepInterval);
  }, []);

  // Progress animation
  useEffect(() => {
    if (currentStep === 2) {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) return 0;
          return prev + 5;
        });
      }, 150);
      return () => clearInterval(progressInterval);
    } else {
      setProgress(0);
    }
  }, [currentStep]);

  return (
    <div className="space-y-4">
      {/* Language Support Banner */}
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {languages.map((lang, i) => (
          <Badge
            key={lang}
            variant="secondary"
            className={cn(
              'text-xs',
              i === 0 && 'bg-primary text-primary-foreground'
            )}
          >
            {lang}
          </Badge>
        ))}
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-between px-4">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const isActive = i === currentStep;
          const isPast = i < currentStep;

          return (
            <div key={step.key} className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300',
                  isActive && 'bg-primary text-primary-foreground scale-110',
                  isPast && 'bg-primary/20 text-primary',
                  !isActive && !isPast && 'bg-muted text-muted-foreground'
                )}
              >
                <Icon className="w-5 h-5" />
              </div>
              <span
                className={cn(
                  'text-xs',
                  isActive ? 'text-primary font-medium' : 'text-muted-foreground'
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div className="bg-muted/50 rounded-xl p-6 min-h-[160px] flex items-center justify-center">
        {currentStep === 0 && (
          <div className="text-center animate-fade-in">
            <div className="text-lg font-medium text-heading mb-2">
              Welcome to your AI Interview
            </div>
            <p className="text-sm text-muted-foreground">
              You'll answer 5 questions about your experience
            </p>
          </div>
        )}

        {currentStep === 1 && (
          <div className="text-center animate-fade-in">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <Video className="w-6 h-6" />
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <Mic className="w-6 h-6" />
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              Camera and microphone ready ✓
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="w-full animate-fade-in">
            <div className="text-center mb-4">
              <div className="text-sm font-medium text-heading mb-1">
                Question 3 of 5
              </div>
              <p className="text-sm text-muted-foreground">
                "Describe a challenging project you led..."
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                <Video className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Recording...</span>
                  <span>{Math.floor(progress / 100 * 120)}s / 120s</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-8 h-8" />
            </div>
            <div className="text-lg font-medium text-heading mb-1">
              Interview Complete!
            </div>
            <p className="text-sm text-muted-foreground">
              AI analysis in progress...
            </p>
          </div>
        )}
      </div>

      {/* AI Analysis Preview */}
      <div className="flex items-center justify-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          <span className="text-muted-foreground">Communication: 92%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          <span className="text-muted-foreground">Relevance: 88%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
          <span className="text-muted-foreground">Depth: 85%</span>
        </div>
      </div>
    </div>
  );
}
